import { useState } from 'react';

import { useChannels, useCreateChannel, useUpdateChannel } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDate } from 'src/lib/time';
import type { EditorialChannelWithStats } from 'src/types/editorial';

/** Answers: which channels does the box watch for a live stream, and how has each one done? */

export function ChannelsWorkspace() {
  const channels = useChannels();

  return (
    <div className="page-medium">
      <PageHeader title="Orígenes">
        Los canales de YouTube que el monitor revisa en busca de una transmisión en vivo. Uno inactivo
        queda registrado pero no se consulta — actívalo cuando quieras volver a escucharlo. Varios
        activos a la vez se capturan al mismo tiempo, no uno tras otro.
      </PageHeader>

      {channels.isPending && <Skeleton variant="rows" count={3} />}
      {channels.isError && <LoadFailure title="No se pudo cargar la lista" error={channels.error} onRetry={channels.refetch} />}

      {channels.data && (
        <ul className="channels">
          {channels.data.channels.length === 0 && <li className="channels__empty">Ningún canal registrado todavía.</li>}
          {channels.data.channels.map(channel => (
            <ChannelRow key={channel.channelId} channel={channel} />
          ))}
        </ul>
      )}

      {/* The roster answers the screen's question; adding one is what you do about the answer. */}
      <NewChannel />
    </div>
  );
}

function NewChannel() {
  const create = useCreateChannel();
  const [channelUrl, setChannelUrl] = useState('');
  const [name, setName] = useState('');

  return (
    <form
      className="channels-form"
      onSubmit={event => {
        event.preventDefault();
        create.mutate(
          { channelUrl: channelUrl.trim(), name: name.trim() },
          {
            onSuccess: () => {
              setChannelUrl('');
              setName('');
            },
          }
        );
      }}
    >
      <p className="channels-form__heading">Agregar canal</p>

      <label className="channels-form__field">
        <span>Liga del canal</span>
        <input
          type="url"
          value={channelUrl}
          onChange={event => setChannelUrl(event.target.value)}
          required
          placeholder="https://www.youtube.com/@gobiernodemexico"
        />
      </label>

      <label className="channels-form__field">
        <span>Nombre</span>
        <input type="text" value={name} onChange={event => setName(event.target.value)} placeholder="La Mañanera" />
      </label>

      <Button type="submit" variant="primary" busy={create.isPending}>
        Registrar
      </Button>

      {create.isError && (
        <Notice tone="contradicted" title="No se pudo registrar" role="alert">
          <p>{create.error instanceof Error ? create.error.message : 'Error desconocido'}</p>
        </Notice>
      )}
    </form>
  );
}

function ChannelRow({ channel }: { channel: EditorialChannelWithStats }) {
  const update = useUpdateChannel();
  const [renaming, setRenaming] = useState(false);
  const active = channel.active;

  return (
    <li className="channel" data-status={active ? 'active' : 'inactive'}>
      <div className="channel__who">
        {renaming ? (
          <RenameChannel channel={channel} onDone={() => setRenaming(false)} />
        ) : (
          <p className="channel__name">
            {channel.name}
            {/* The name is the only field an editor owns here — the URL is YouTube's and the id is
                derived from it — so renaming lives on the name itself rather than behind a menu. */}
            <button type="button" className="channel__rename" onClick={() => setRenaming(true)}>
              Renombrar
            </button>
          </p>
        )}
        <a className="channel__url mono" href={channel.channelUrl} target="_blank" rel="noreferrer noopener">
          {channel.channelUrl}
        </a>
      </div>

      <div className="channel__stats">
        <Badge tone={active ? 'supported' : 'unresolved'}>{active ? 'Activo' : 'Inactivo'}</Badge>
        <span className="channel__stat">{channel.stats.streamCount} transmisión{channel.stats.streamCount === 1 ? '' : 'es'}</span>
        <span className="channel__stat">{formatDurationMinutes(channel.stats.avgDurationMinutes)} en promedio</span>
        <span className="channel__since">
          {channel.stats.lastStreamAt ? `en vivo por última vez ${formatDate(channel.stats.lastStreamAt)}` : 'nunca ha estado en vivo'}
        </span>
      </div>

      <div className="channel__actions">
        <Button
          variant="ghost"
          className={active ? 'channel__withdraw' : undefined}
          busy={update.isPending}
          onClick={() => update.mutate({ channelId: channel.channelId, active: !active })}
        >
          {active ? 'Pausar' : 'Activar'}
        </Button>
      </div>

      {update.isError && <p className="channel__error">{update.error instanceof Error ? update.error.message : 'No se pudo cambiar'}</p>}
    </li>
  );
}

/**
 * Renaming is display-only: `channelId` is derived from the URL and never changes, so every stream
 * already captured keeps its attribution. That is what makes this safe to do inline — there is no
 * migration behind it, and a typo is fixed by typing again.
 */
function RenameChannel({ channel, onDone }: { channel: EditorialChannelWithStats; onDone: () => void }) {
  const update = useUpdateChannel();
  const [name, setName] = useState(channel.name);
  const trimmed = name.trim();

  return (
    <form
      className="channel__rename-form"
      onSubmit={event => {
        event.preventDefault();
        if (!trimmed || trimmed === channel.name) {
          onDone();
          return;
        }
        update.mutate({ channelId: channel.channelId, name: trimmed }, { onSuccess: onDone });
      }}
    >
      <input
        type="text"
        value={name}
        onChange={event => setName(event.target.value)}
        aria-label={`Nuevo nombre para ${channel.name}`}
        maxLength={120}
        autoFocus
        onKeyDown={event => {
          if (event.key === 'Escape') onDone();
        }}
      />
      <Button type="submit" variant="primary" busy={update.isPending} disabled={!trimmed}>
        Guardar
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        Cancelar
      </Button>
      {update.isError && (
        <p className="channel__error">{update.error instanceof Error ? update.error.message : 'No se pudo renombrar'}</p>
      )}
    </form>
  );
}

function formatDurationMinutes(minutes: number | null): string {
  if (minutes === null) return 'sin datos de duración';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}
